import { BoardApi } from "@/api/board/BoardApi";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useControlBoard() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["board", "control", user?.id ?? ""],
    queryFn: () => BoardApi.listControlBoard(),
    enabled: !!user?.id,
    staleTime: 15_000,
  });
  return {
    board: q.data ?? null,
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useCaseOpenTasks(caseId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ["board", "caseTasks", caseId ?? ""],
    queryFn: () => BoardApi.listCaseOpenTasks(caseId!),
    enabled: !!caseId && enabled,
    staleTime: 10_000,
  });
  return { tasks: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

export function useNotifications() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["board", "notifications", user?.id ?? ""],
    queryFn: async () => {
      try {
        await BoardApi.sweepCareDueNotifications();
      } catch {
        /* sweep best-effort */
      }
      return BoardApi.listNotifications();
    },
    enabled: !!user?.id,
    staleTime: 20_000,
  });
  return {
    notifications: q.data ?? [],
    unread: (q.data ?? []).filter((n) => !n.read_at).length,
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useSetBoardDensity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (density: "compact" | "expanded") =>
      BoardApi.setBoardDensity(density),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["board", "control"] });
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => BoardApi.markNotificationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["board", "notifications"] });
    },
  });
}

export function useCareTaskRules(organizationId: string | undefined) {
  const q = useQuery({
    queryKey: ["board", "careTaskRules", organizationId ?? ""],
    queryFn: () => BoardApi.listCareTaskRules(organizationId!),
    enabled: !!organizationId,
  });
  return { rules: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

export function useUpsertCareTaskRule(organizationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: BoardApi.upsertCareTaskRule,
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["board", "careTaskRules", organizationId ?? ""],
      });
    },
  });
}

export function useCaseTaskOverrides(caseId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ["board", "caseOverrides", caseId ?? ""],
    queryFn: () => BoardApi.listCaseOverrides(caseId!),
    enabled: !!caseId && enabled,
  });
  return { overrides: q.data ?? [], refetch: q.refetch };
}

export function useUpsertCaseOverride(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: BoardApi.upsertCaseOverride,
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["board", "caseOverrides", caseId ?? ""],
      });
      void qc.invalidateQueries({ queryKey: ["board", "caseTasks", caseId ?? ""] });
      void qc.invalidateQueries({ queryKey: ["board", "control"] });
    },
  });
}
